import { supabase } from '../supabase'
import { INGREDIENT_CARDS } from '../../games/ideatthat/data/ingredientCards'
import type { RoomRow, RoomPlayerRow, SubmissionRow, CourseVoteRow } from '../database.types'

/** Full snapshot of a live room + its people + their submissions. */
export interface RoomSnapshot {
  room: RoomRow
  players: RoomPlayerRow[]
  submissions: SubmissionRow[]
  courseVotes: CourseVoteRow[]
}

// Pool of IDs the server picks hands from. Server-side RNG means the
// client can't cheat by choosing favourable cards.
const INGREDIENT_POOL = INGREDIENT_CARDS.map(c => c.id)

export async function createRoom(isPrivate: boolean): Promise<RoomRow> {
  const { data, error } = await supabase.rpc('create_room', { p_is_private: isPrivate })
  if (error) throw error
  return data as RoomRow
}

export async function joinRoomByCode(code: string): Promise<RoomRow> {
  const { data, error } = await supabase.rpc('join_room_by_code', { p_code: code.toUpperCase() })
  if (error) throw error
  return data as RoomRow
}

export async function joinQuickPlay(): Promise<RoomRow> {
  const { data, error } = await supabase.rpc('join_quick_play')
  if (error) throw error
  return data as RoomRow
}

export async function leaveRoom(roomId: string): Promise<void> {
  const { error } = await supabase.rpc('leave_room', { p_room_id: roomId })
  if (error) throw error
}

/**
 * Hand size per the V1 rulebook: 9 cards = 3 per course × 3 courses.
 * Bonus mechanic (TODO): if all OTHER players "I'd eat that"-vote one
 * of your courses, you draw +1 card. Currently we deal 9 up front and
 * skip the bonus pull because the live state machine writes all 3
 * courses at once before the votes land — leaving the bonus rule for
 * a future restructured flow that writes-presents-votes per course.
 */
export const IET_HAND_SIZE = 9

export async function startGame(roomId: string): Promise<void> {
  const { error } = await supabase.rpc('start_game', {
    p_room_id: roomId,
    p_ingredient_pool: INGREDIENT_POOL,
    p_hand_size: IET_HAND_SIZE,
  })
  if (error) throw error
}

export async function submitCourses(
  roomId: string,
  c1: { text: string; ids: string[] },
  c2: { text: string; ids: string[] },
  c3: { text: string; ids: string[] },
): Promise<void> {
  const { error } = await supabase.rpc('submit_courses', {
    p_room_id:  roomId,
    p_c1_text:  c1.text, p_c1_ids: c1.ids,
    p_c2_text:  c2.text, p_c2_ids: c2.ids,
    p_c3_text:  c3.text, p_c3_ids: c3.ids,
  })
  if (error) throw error
}

/**
 * Per-course partial submission. Used by the new one-course-at-a-time
 * flow so we don't force players to write everything up front.
 */
export async function submitSingleCourse(
  roomId: string,
  course: 1 | 2 | 3,
  text: string,
  ids: string[],
): Promise<void> {
  const { error } = await supabase.rpc('submit_single_course', {
    p_room_id: roomId,
    p_course:  course,
    p_text:    text,
    p_ids:     ids,
  })
  if (error) throw error
}

export async function advancePhase(
  roomId: string,
  expected?: { status?: string; course?: number; index?: number },
): Promise<RoomRow> {
  const { data, error } = await supabase.rpc('advance_phase', {
    p_room_id: roomId,
    p_expected_status: expected?.status ?? null,
    p_expected_course: expected?.course ?? null,
    p_expected_index: expected?.index ?? null,
  })
  if (error) throw error
  return data as RoomRow
}

/** Step the presenting walk one presenter backward (recovery for an
 *  accidental double-advance). Any player may call it. */
export async function stepBackPresenter(roomId: string, expectedIndex?: number): Promise<RoomRow> {
  // Cast: step_back_presenter isn't in the generated RPC types yet.
  const { data, error } = await (supabase as any).rpc('step_back_presenter', {
    p_room_id: roomId,
    p_expected_index: expectedIndex ?? null,
  })
  if (error) throw error
  return data as RoomRow
}

/**
 * Soft-skip a dropped seat so the room can advance (any player may call
 * it — the host may be the one who dropped). Keeps the seat's votes; just
 * excludes it from the everyone-in / presenter counts.
 */
export async function kickIetPlayer(playerId: string): Promise<RoomRow> {
  const { data, error } = await (supabase as any).rpc('kick_iet_player', { p_player_id: playerId })
  if (error) throw error
  return data as RoomRow
}

export async function castCourseVote(
  submissionId: string,
  course: 1 | 2 | 3,
  wouldEat: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('cast_course_vote', {
    p_submission_id: submissionId,
    p_course:        course,
    p_would_eat:     wouldEat,
  })
  if (error) throw error
}

/** Fetch a room and everything attached to it. */
export async function fetchRoomSnapshot(roomId: string): Promise<RoomSnapshot | null> {
  const [roomRes, playersRes, subsRes] = await Promise.all([
    supabase.from('rooms').select('*').eq('id', roomId).single(),
    supabase.from('room_players').select('*').eq('room_id', roomId).order('join_order'),
    supabase.from('submissions').select('*').eq('room_id', roomId),
  ])

  if (roomRes.error || !roomRes.data) return null

  const submissionIds = (subsRes.data ?? []).map(s => s.id)
  let courseVotes: CourseVoteRow[] = []
  if (submissionIds.length > 0) {
    const votesRes = await supabase
      .from('course_votes')
      .select('*')
      .in('submission_id', submissionIds)
    courseVotes = votesRes.data ?? []
  }

  return {
    room:        roomRes.data as RoomRow,
    players:     (playersRes.data ?? []) as RoomPlayerRow[],
    submissions: (subsRes.data ?? []) as SubmissionRow[],
    courseVotes,
  }
}

/**
 * Subscribe to realtime changes on a room + its players + submissions.
 * Returns an unsubscribe function.
 */
export function subscribeToRoom(
  roomId: string,
  onChange: () => void,
): () => void {
  const channel = supabase
    .channel(`room:${roomId}`)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'rooms',        filter: `id=eq.${roomId}` },
        onChange)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'room_players', filter: `room_id=eq.${roomId}` },
        onChange)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'submissions',  filter: `room_id=eq.${roomId}` },
        onChange)
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'course_votes' },
        onChange) // filter by submission_id not available; rely on room_id join
    .subscribe()

  return () => { supabase.removeChannel(channel) }
}
